import { decrypt } from './crypto'
import { toast } from 'sonner'
import useTokenStore from '@/store/store'

type Method = 'GET' | 'POST' | 'PUT' | 'DELETE'
type Body = Record<string, any>

interface RequestOptions {
  url: string
  method?: Method
  body?: Body | FormData
  query?: Record<string, string>
  responseType?: 'json' | 'text' | 'blob' | 'sse'
  contentType?: 'json' | 'form' // 新增：指定请求内容类型
  onMessage?: (event: MessageEvent) => void
  onError?: (error: Error) => void
  onSuccess?: () => void
  signal?: AbortSignal
  skipAuthRedirect?: boolean
}

// 获取当前环境 development production
const isDev = import.meta.env.DEV
// 开发环境下，使用 /api 前缀
const devApiPrefix = '/api'
const BASE_URL = import.meta.env.VITE_API_URL + '/api'

export const getToken = () => {
  const token = useTokenStore.getState().token
  if (token) {
    return decrypt(token)
  }
  return null
}

const handleAuthError = () => {
  useTokenStore.getState().clearToken()
  // 显示错误提示，并在短暂延迟后跳转登录页，保证用户可见
  toast.error('登录已过期，请重新登录', { duration: 1500 })
  const { origin } = window.location
  let base = import.meta.env.BASE_URL || '/'
  if (base === './') {
    base = '/'
  }
  const normalizedBase = base.endsWith('/') ? base.slice(0, -1) : base
  setTimeout(() => {
    window.location.href = `${origin}${normalizedBase}/login`
  }, 1600)
}

export const request = async <T = undefined>({
  method = 'GET',
  url,
  body,
  query,
  responseType = 'json',
  contentType = 'json', // 默认使用 JSON 格式
  onMessage,
  signal,
  skipAuthRedirect = false // 是否跳过认证错误
}: RequestOptions): Promise<T> => {
  let apiUrl = new URL(`${BASE_URL}${url}`)
  if (query) {
    const params = new URLSearchParams()
    for (const key in query) {
      const value = query[key]
      if (value !== '') {
        params.append(key, value as any)
      }
    }
    apiUrl.search = params.toString()
  }

  try {
    if (responseType === 'sse') {
      const headers: HeadersInit = {
        'Content-Type': 'text/event-stream',
        'Cache-Control': 'no-cache',
        Connection: 'keep-alive'
      }

      const token = getToken()
      if (token) {
        headers.Authorization = token
      }

      const response = await fetch(apiUrl.toString(), {
        method: 'GET',
        headers,
        signal
      })

      if ((response.status === 401 || response.status === 403) && !skipAuthRedirect) {
        handleAuthError()
        throw new Error('认证失败，请重新登录')
      }

      if (!response.ok) {
        // 尝试解析错误响应体，提取 message 字段
        let errorMessage = `HTTP error! status: ${response.status}`
        try {
          const errorData = await response.json()
          if (errorData.message) {
            errorMessage = errorData.message
          }
        } catch (parseError) {
          // 如果解析失败，使用默认错误消息
        }
        throw new Error(errorMessage)
      }

      const reader = response.body?.getReader()

      const streamPromise = new Promise<void>((resolve, reject) => {
        const stream = new ReadableStream({
          start(controller) {
            function push() {
              reader
                ?.read()
                .then(({ done, value }) => {
                  if (done) {
                    controller.close()
                    resolve()
                    return
                  }
                  controller.enqueue(value)
                  push()
                })
                .catch(reject)
            }
            push()
          }
        })

        const textDecoder = new TextDecoder()

        new Response(stream).body
          ?.pipeTo(
            new WritableStream({
              write(chunk) {
                const text = textDecoder.decode(chunk)
                const events = text.split('\n\n')
                for (const event of events) {
                  if (event.trim() === '') continue
                  const [, data] = event.split('data: ')
                  if (data) {
                    onMessage?.(new MessageEvent('message', { data }))
                  }
                }
              }
            })
          )
          .catch(reject)
      })

      await streamPromise
      return {} as T;
    } else {
      const headers: HeadersInit = {}

      // 根据 contentType 和 body 类型设置 Content-Type 和序列化方式
      let requestBody: string | FormData | undefined

      if (body) {
        if (body instanceof FormData) {
          // FormData 不设置 Content-Type，让浏览器自动设置（包括 boundary）
          requestBody = body
        } else if (contentType === 'form') {
          // 使用 application/x-www-form-urlencoded 格式
          headers['Content-Type'] = 'application/x-www-form-urlencoded'
          const formData = new URLSearchParams()
          for (const key in body) {
            formData.append(key, String(body[key]))
          }
          requestBody = formData.toString()
        } else {
          // 默认使用 JSON 格式
          headers['Content-Type'] = 'application/json'
          requestBody = JSON.stringify(body)
        }
      }

      const token = getToken()
      if (token) {
        headers.Authorization = token
      }

      const response = await fetch(apiUrl.toString(), {
        method,
        headers,
        body: requestBody,
        signal
      })

      if ((response.status === 401 || response.status === 403) && !skipAuthRedirect) {
        handleAuthError()
        throw new Error('认证失败，请重新登录')
      }

      if (!response.ok) {
        // 尝试解析错误响应体，提取 message 字段
        let errorMessage = `HTTP error! status: ${response.status}`
        try {
          const errorData = await response.json()
          if (errorData.message) {
            errorMessage = errorData.message
          }
        } catch (parseError) {
          // 如果解析失败，使用默认错误消息
        }
        throw new Error(errorMessage)
      }

      if (responseType === 'text') {
        return response.text().then((text) => text as unknown as T);
      } else if (responseType === 'blob') {
        return response.blob().then((blob) => (blob as unknown) as T);
      } else {
        return response.json().then((json) => json as unknown as T).catch((error) => {
          console.error('解析响应失败:', error)
          return '' as unknown as T
        })
      }
    }
  } catch (error) {
    throw error instanceof Error ? error : new Error('发生未知错误')
  }
}
